import React, { useState } from 'react'
import axios from 'axios'
import ComponentBuilder from '../LineItems/ComponentBuilder'
import RecipeBuilderV2 from '../ServiceRecipes/v2/RecipeBuilderV2'
import MarkupCell from '../../estimating/MarkupCell'
import { tokens } from '../../../styles/desertFloraTheme'

const fmt = (v) => '$' + (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })

const COST_TYPE_COLORS = {
  labor:       { color: '#3454c1', bg: '#e6ebfa' },
  equipment:   { color: '#b35d1a', bg: '#fbe8d6' },
  material:    { color: '#2f7a3e', bg: '#dceedf' },
  subcontract: { color: '#6e3fb7', bg: '#ece2fa' },
  disposal:    { color: '#a9341d', bg: '#f7dccf' }
}

const mono = {
  fontFamily: tokens.fontMono,
  fontVariantNumeric: 'tabular-nums'
}

const headerCellBase = {
  fontFamily: tokens.fontMono,
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: tokens.ink3,
  padding: '8px 12px',
  background: tokens.surface2,
  borderBottom: '1px solid ' + tokens.line
}

const cellBase = {
  padding: '8px 12px',
  borderBottom: '1px solid ' + tokens.line,
  fontSize: 12,
  verticalAlign: 'middle'
}

const COL = {
  type:   { width: 90 },
  desc:   { flex: 1 },
  role:   { width: 100 },
  qty:    { width: 70, textAlign: 'right' },
  rate:   { width: 80, textAlign: 'right' },
  hours:  { width: 60, textAlign: 'right' },
  ext:    { width: 90, textAlign: 'right' },
  markup: { width: 70, textAlign: 'right' },
  sell:   { width: 90, textAlign: 'right' },
  edit:   { width: 40, textAlign: 'center' }
}

const CostTypeChip = ({ costType }) => {
  const ct = COST_TYPE_COLORS[costType] || { color: tokens.ink3, bg: tokens.surface2 }
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '2px 8px 2px 6px',
      borderRadius: 999,
      fontFamily: tokens.fontMono,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '.04em',
      textTransform: 'uppercase',
      border: '1px solid ' + ct.color,
      backgroundColor: ct.bg,
      color: ct.color
    }}>
      <span style={{
        width: 5,
        height: 5,
        borderRadius: '50%',
        background: ct.color,
        flexShrink: 0
      }} />
      {costType}
    </span>
  )
}

const Dash = () => <span style={{ ...mono, color: tokens.ink4 }}>&mdash;</span>

const ComponentRecipeSummary = ({ projectService, project, url, onUpdate }) => {
  const [editIndex, setEditIndex] = useState(null)
  const [recipeModalServiceId, setRecipeModalServiceId] = useState(null)

  const components = projectService.components || []

  const syntheticLineItem = {
    _id: projectService._id,
    quantity: projectService.totalQuantity || 0,
    unit: projectService.unit || ''
  }

  const handleSave = async (payload) => {
    const updatedComponents = [...components]
    updatedComponents[editIndex] = { ...updatedComponents[editIndex], ...payload }

    try {
      await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}`, {
        components: updatedComponents
      })
      setEditIndex(null)
      onUpdate()
    } catch (e) {
      console.error('Component save failed:', e)
    }
  }

  const handleMarkupChange = async (index, newMarkup) => {
    const updatedComponents = [...components]
    const comp = { ...updatedComponents[index] }
    comp.markupPct = newMarkup
    const ext = comp.extendedCost || 0
    comp.sellPrice = ext * (1 + newMarkup / 100)
    updatedComponents[index] = comp

    try {
      await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}`, {
        components: updatedComponents
      })
      onUpdate()
    } catch (e) {
      console.error('Markup save failed:', e)
    }
  }

  const included = components.filter(c => c.included !== false)
  const totalCost = included.reduce((s, c) => s + (c.extendedCost || 0), 0)
  const totalSell = included.reduce((s, c) => s + (c.sellPrice || 0), 0)
  const qty = projectService.totalQuantity || 0
  const unit = projectService.unit || 'unit'

  const isModified = (c) => {
    if (c._mult && c._mult !== 1) return true
    if (c.quantityModifier && c.quantityModifier !== 1) return true
    return false
  }

  const getModifierLabel = (c) => {
    const mult = c._mult || c.quantityModifier
    if (!mult || mult === 1) return null
    const arrow = mult > 1 ? '↑' : '↓'
    return `${arrow} ×${mult}`
  }

  return (
    <div style={{
      background: tokens.surface,
      border: '1px solid ' + tokens.line,
      borderRadius: 14,
      overflow: 'hidden'
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: COL.type.width }} />
          <col />
          <col style={{ width: COL.role.width }} />
          <col style={{ width: COL.qty.width }} />
          <col style={{ width: COL.rate.width }} />
          <col style={{ width: COL.hours.width }} />
          <col style={{ width: COL.ext.width }} />
          <col style={{ width: COL.markup.width }} />
          <col style={{ width: COL.sell.width }} />
          <col style={{ width: COL.edit.width }} />
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...headerCellBase, textAlign: 'left' }}>Type</th>
            <th style={{ ...headerCellBase, textAlign: 'left' }}>Description</th>
            <th style={{ ...headerCellBase, textAlign: 'left' }}>Role / Item</th>
            <th style={{ ...headerCellBase, textAlign: 'right' }}>Qty</th>
            <th style={{ ...headerCellBase, textAlign: 'right' }}>Rate</th>
            <th style={{ ...headerCellBase, textAlign: 'right' }}>Hours</th>
            <th style={{ ...headerCellBase, textAlign: 'right' }}>Ext Cost</th>
            <th style={{ ...headerCellBase, textAlign: 'right' }}>Markup</th>
            <th style={{ ...headerCellBase, textAlign: 'right' }}>Sell</th>
            <th style={{ ...headerCellBase, textAlign: 'center' }}>Edit</th>
          </tr>
        </thead>
        <tbody>
          {components.map((c, i) => {
            const mp = c.materialPricing || {}
            const rate = c.productionRate?.hourlyRate || mp.manualOverride || mp.unitCost || c.subcontractPricing?.unitCost || 0
            const hours = c.productionRate?.hours
            const role = c.productionRate?.role || mp.selectedVendor || c.subcontractPricing?.subName || ''

            const isExcluded = c.included === false
            const modified = isModified(c)
            const modLabel = getModifierLabel(c)

            const isUnassignedMaterial = c.costType === 'material' && !mp.catalogItemId && !mp.manualOverride

            const rowStyle = {
              ...(isExcluded ? { textDecoration: 'line-through', opacity: 0.4 } : {})
            }

            const firstCellExtra = modified ? { borderLeft: '3px solid ' + tokens.accent } : {}

            return (
              <tr key={i} style={rowStyle}>
                {/* Type chip */}
                <td style={{ ...cellBase, ...firstCellExtra }}>
                  <CostTypeChip costType={c.costType} />
                </td>

                {/* Description */}
                <td style={{ ...cellBase, fontWeight: 500, color: tokens.ink }}>
                  <div>
                    <span>{c.description || '—'}</span>
                    {modified && modLabel && (
                      <span style={{
                        marginLeft: 6,
                        fontSize: 10,
                        background: tokens.accentSoft,
                        color: tokens.accent,
                        padding: '1px 6px',
                        borderRadius: 999,
                        fontFamily: tokens.fontMono
                      }}>{modLabel}</span>
                    )}
                  </div>
                  {isExcluded && (
                    <div style={{ fontStyle: 'italic', fontSize: 11, color: tokens.ink3, fontWeight: 400, textDecoration: 'none' }}>
                      Excluded{c.excludeReason ? ' — ' + c.excludeReason : ''}
                    </div>
                  )}
                  {isUnassignedMaterial && !isExcluded && (
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginTop: 2 }}
                      onClick={() => setEditIndex(i)}
                    >
                      <span style={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: '#8c5b00',
                        flexShrink: 0
                      }} />
                      <span style={{ fontStyle: 'italic', fontSize: 11, color: tokens.ink3, fontWeight: 400 }}>
                        Click to assign material
                      </span>
                    </div>
                  )}
                </td>

                {/* Role / Item */}
                <td style={{ ...cellBase, color: tokens.ink2, fontSize: 11 }}>
                  {role || <Dash />}
                </td>

                {/* Qty */}
                <td style={{ ...cellBase, ...mono, textAlign: 'right' }}>
                  {c.componentQty ? c.componentQty : <Dash />}
                </td>

                {/* Rate */}
                <td style={{ ...cellBase, ...mono, textAlign: 'right' }}>
                  {rate ? fmt(rate) : <Dash />}
                </td>

                {/* Hours */}
                <td style={{ ...cellBase, ...mono, textAlign: 'right' }}>
                  {hours != null && hours > 0 ? hours + 'h' : <Dash />}
                </td>

                {/* Ext Cost */}
                <td style={{ ...cellBase, ...mono, textAlign: 'right', color: tokens.ink }}>
                  {c.extendedCost ? fmt(c.extendedCost) : <Dash />}
                </td>

                {/* Markup */}
                <td style={{ ...cellBase, textAlign: 'right' }}>
                  <MarkupCell
                    value={c.markupPct || 0}
                    onChange={(v) => handleMarkupChange(i, v)}
                    disabled={isExcluded}
                  />
                </td>

                {/* Sell */}
                <td style={{
                  ...cellBase,
                  ...mono,
                  textAlign: 'right',
                  color: tokens.primary,
                  fontWeight: 600
                }}>
                  {c.sellPrice ? fmt(c.sellPrice) : <Dash />}
                </td>

                {/* Edit */}
                <td style={{ ...cellBase, textAlign: 'center' }}>
                  <span
                    style={{
                      cursor: 'pointer',
                      color: tokens.ink4,
                      fontSize: 14,
                      lineHeight: 1,
                      transition: 'color .12s'
                    }}
                    onClick={() => setEditIndex(i)}
                    onMouseEnter={(e) => { e.currentTarget.style.color = tokens.primary }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = tokens.ink4 }}
                    title="Edit component"
                  >
                    &#9998;
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: tokens.surface2 }}>
            <td colSpan={6} style={{
              padding: '10px 12px',
              fontSize: 13,
              fontWeight: 600,
              color: tokens.ink,
              borderBottom: 'none'
            }}>
              Totals &middot; {qty} {unit}
            </td>
            <td style={{
              padding: '10px 12px',
              textAlign: 'right',
              ...mono,
              fontSize: 14,
              fontWeight: 700,
              color: tokens.ink,
              borderBottom: 'none'
            }}>
              {fmt(totalCost)}
            </td>
            <td style={{ padding: '10px 12px', borderBottom: 'none' }} />
            <td style={{
              padding: '10px 12px',
              textAlign: 'right',
              ...mono,
              fontSize: 14,
              fontWeight: 700,
              color: tokens.primary,
              borderBottom: 'none'
            }}>
              {fmt(totalSell)}
            </td>
            <td style={{ padding: '10px 12px', borderBottom: 'none' }} />
          </tr>
        </tfoot>
      </table>

      {/* Recipe footer */}
      <div style={{
        padding: '10px 16px',
        borderTop: '1px solid ' + tokens.line,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ fontSize: 11, color: tokens.ink3, fontFamily: tokens.fontMono }}>
          {qty > 0 && totalCost > 0 ? (
            <>Per-unit &middot; {fmt(totalCost / qty)}/{unit} cost &middot; {fmt(totalSell / qty)}/{unit} sell</>
          ) : (
            <>Per-unit &middot; &mdash;</>
          )}
        </span>
        {projectService.serviceId && (
          <span
            style={{
              fontSize: 11,
              color: tokens.primary,
              cursor: 'pointer',
              fontFamily: tokens.fontMono
            }}
            onClick={() => setRecipeModalServiceId(projectService.serviceId)}
          >
            Inherits from {projectService.serviceId}
          </span>
        )}
      </div>

      {/* ComponentBuilder modal */}
      {editIndex !== null && (
        <ComponentBuilder
          open={true}
          onClose={() => setEditIndex(null)}
          onSave={handleSave}
          lineItem={syntheticLineItem}
          project={project}
          url={url}
          editComponent={components[editIndex]}
          externalSave
        />
      )}

      {/* RecipeBuilderV2 modal */}
      {recipeModalServiceId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'var(--bg-page, #f5f5f5)' }}>
          <RecipeBuilderV2
            initialServiceId={recipeModalServiceId}
            modal
            onClose={() => { setRecipeModalServiceId(null); onUpdate() }}
          />
        </div>
      )}
    </div>
  )
}

export default ComponentRecipeSummary
